import "../../../chunk-D57E6H3M.mjs";

// src/components/composite/Conversations/ConversationMessages.tsx
import { memo, useCallback, useMemo } from "react";
import { MdChatBubbleOutline } from "react-icons/md";
import { ChatRole, shouldRenderChatMessage } from "../../../domain/index.mjs";
import { useNow } from "../../../hooks/useNow.mjs";
import { ComponentsI18nProvider } from "../../../i18n/index.mjs";
import { formatTimestamp } from "../../../utils/formatTimestamp.mjs";
import { EmptyState, EmptyStateDescription, EmptyStateMedia, Link, Skeleton, Tooltip } from "../../primitive/index.mjs";
import { ChatMessage } from "../ChatMessage/ChatMessage.mjs";
import { ProductMentionChip, ProductMentionText } from "../Timeline/ContentChips/index.mjs";
import { MessageAttachments } from "./MessageAttachments.mjs";
import { useConversationsTranslation } from "./use-conversations-translation.mjs";
import { feedbackLabel, tokenUsageText } from "./utils.mjs";
import { jsx, jsxs } from "react/jsx-runtime";
function assistantMessage(message) {
  const timeline = message.metadata?.timeline?.length ? message.metadata.timeline : message.content.trim() ? [{ type: "content", content: message.content, timestamp: Date.parse(message.createdAt) }] : [];
  return { id: message.id, role: ChatRole.Assistant, content: message.content, timeline, source: message.source ?? void 0 };
}
var MESSAGE_STYLE = { marginBottom: 0 };
var PLACEHOLDER_BUBBLES = [
  { user: false, width: "62%", height: 70 },
  { user: true, width: "38%", height: 46 },
  { user: false, width: "54%", height: 46 }
];
function ConversationMessagesImpl({ activeChatSession, loading, showTokenUsage, storeDomain, dashboardMode = true, onToolClick }) {
  const { t, i18n } = useConversationsTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const now = useNow();
  const stampOf = (value) => {
    const stamp = formatTimestamp(value, { now, locale, time: true });
    return stamp ? /* @__PURE__ */ jsx("time", { dateTime: stamp.dateTime, title: stamp.title, children: stamp.text }) : null;
  };
  const messages = activeChatSession.chatMessage;
  const productMap = useMemo(() => {
    const products = {};
    const add = (product) => {
      if (product.title) products[product.title] = product;
    };
    for (const message of messages) {
      message.metadata?.mentionedProducts?.forEach(add);
      if (message.metadata?.currentProduct) add(message.metadata.currentProduct);
      for (const item of message.metadata?.timeline ?? []) {
        if (item.type === "product_card") add(item.product);
        if (item.type === "product_list") item.products.forEach(add);
      }
    }
    return products;
  }, [messages]);
  const sessionID = activeChatSession.id;
  const handleToolClick = useCallback((toolCallId) => onToolClick?.(sessionID, toolCallId), [onToolClick, sessionID]);
  const placeholders = loading && !messages.length;
  return /* @__PURE__ */ jsxs("div", { className: "bs-conversations__messages", role: placeholders ? void 0 : "log", "aria-busy": loading, children: [
    placeholders ? PLACEHOLDER_BUBBLES.map((bubble, i) => /* @__PURE__ */ jsx("div", { className: "bs-conversations__message", "data-user": bubble.user, "aria-hidden": "true", children: /* @__PURE__ */ jsx(Skeleton, { variant: "rect", className: "bs-conversations__bubble-placeholder", width: bubble.width, height: bubble.height }) }, i)) : messages.map((message) => {
      const isUser = message.role === "user";
      if (message.role === "system" || message.metadata?.triggeredByUpsellAction) return /* @__PURE__ */ jsxs("div", { className: "bs-conversations__system", children: [
        /* @__PURE__ */ jsx(ProductMentionText, { content: message.content, productMap, storeDomain }),
        stampOf(message.createdAt)
      ] }, message.id);
      const rendered = isUser ? { id: message.id, role: ChatRole.User, content: message.content, source: message.source ?? void 0 } : assistantMessage(message);
      const showBubble = shouldRenderChatMessage(rendered);
      if (!showBubble && !message.attachments?.length) return null;
      const replies = message.metadata?.timeline?.find((item) => item.type === "possible_replies");
      const usage = message.metadata?.tokenUsage;
      return /* @__PURE__ */ jsxs("div", { className: "bs-conversations__message", "data-user": isUser, children: [
        showBubble && /* @__PURE__ */ jsx(ComponentsI18nProvider, { children: /* @__PURE__ */ jsx(ChatMessage, { message: rendered, isUser, showFadeAnimation: false, style: MESSAGE_STYLE, productMap, storeDomain, dashboardMode, onToolClick: onToolClick ? handleToolClick : void 0 }) }),
        !!message.attachments?.length && /* @__PURE__ */ jsx(MessageAttachments, { attachments: message.attachments }),
        /* @__PURE__ */ jsxs("div", { className: "bs-conversations__message-meta", children: [
          stampOf(message.createdAt),
          !isUser && (message.feedback === "like" || message.feedback === "dislike") && /* @__PURE__ */ jsx("span", { children: feedbackLabel(message, t) }),
          !isUser && message.sentBy === "human" && /* @__PURE__ */ jsx("span", { children: t("shared.chat.log.sent_by_you") }),
          !isUser && message.sentBy === "system" && /* @__PURE__ */ jsx("span", { children: t("shared.chat.log.sent_by_system") }),
          !isUser && replies?.type === "possible_replies" && !!replies.possibleReplies.length && /* @__PURE__ */ jsx(Tooltip, { title: replies.possibleReplies.join("\n"), children: /* @__PURE__ */ jsx("button", { type: "button", children: t("shared.chat.log.suggested_replies") }) }),
          !isUser && showTokenUsage && usage?.totalTokens !== void 0 && /* @__PURE__ */ jsx(Tooltip, { title: tokenUsageText(usage, t, locale), children: /* @__PURE__ */ jsx("button", { type: "button", children: t("shared.chat.token_usage.tokens", { count: usage.totalTokens, value: usage.totalTokens.toLocaleString(locale) }) }) })
        ] }),
        isUser && message.pageUrl && !message.metadata?.currentProduct && /* @__PURE__ */ jsxs("div", { className: "bs-conversations__page-link", children: [
          t("shared.chat.log.page_visited_label"),
          " ",
          /* @__PURE__ */ jsx(Link, { variant: "muted", external: true, href: message.pageUrl, children: message.pageUrl })
        ] }),
        isUser && message.metadata?.currentProduct && /* @__PURE__ */ jsxs("div", { className: "bs-conversations__page-link", children: [
          t("shared.chat.log.viewing_product_label"),
          " ",
          /* @__PURE__ */ jsx(ProductMentionChip, { product: { ...message.metadata.currentProduct, imageUrl: message.metadata.currentProduct.imageUrl ?? void 0 }, storeDomain, size: "label" })
        ] })
      ] }, message.id);
    }),
    !loading && !messages.length && /* @__PURE__ */ jsxs(EmptyState, { variant: "plain", children: [
      /* @__PURE__ */ jsx(EmptyStateMedia, { children: /* @__PURE__ */ jsx(MdChatBubbleOutline, {}) }),
      /* @__PURE__ */ jsx(EmptyStateDescription, { children: t("shared.chat.log.empty_state") })
    ] })
  ] }, placeholders ? "placeholders" : sessionID);
}
var ConversationMessages = memo(ConversationMessagesImpl);
ConversationMessages.displayName = "ConversationMessages";
export {
  ConversationMessages
};
//# sourceMappingURL=ConversationMessages.mjs.map